const API_BASE = 'https://mainnet.zklighter.elliot.ai/api/v1'

const marketIdCache = new Map()

export async function resolveMarketId(symbol) {
  const key = String(symbol || '').toUpperCase()
  if (!key) return null
  if (marketIdCache.has(key)) return marketIdCache.get(key)
  const market = await fetchMarket(symbol)
  return market?.market_id ?? null
}

export async function fetchMarket(symbol) {
  const key = String(symbol || '').toUpperCase()
  if (!key) return null

  const res = await fetch(`${API_BASE}/orderBookDetails`)
  if (!res.ok) throw new Error(`orderBookDetails ${res.status}`)
  const json = await res.json()
  const books = json.order_book_details || []
  const match = books.find((m) => String(m.symbol || '').toUpperCase() === key)
  if (match?.market_id != null) marketIdCache.set(key, match.market_id)
  return match || null
}

export async function fetchOrderBookDetails() {
  const res = await fetch(`${API_BASE}/orderBookDetails`)
  if (!res.ok) throw new Error(`orderBookDetails ${res.status}`)
  const json = await res.json()
  return json.order_book_details || []
}

export async function fetchTokenList() {
  const res = await fetch(`${API_BASE}/tokenlist`)
  if (!res.ok) throw new Error(`tokenlist ${res.status}`)
  const json = await res.json()
  return json.tokens || []
}

export async function fetchMarkets() {
  const books = await fetchOrderBookDetails()
  return books.map((m) => {
    const imf = Number(m.default_initial_margin_fraction)
    const leverage = imf > 0 ? Math.round(10_000 / imf) : 5
    return {
      symbol: String(m.symbol || '').toUpperCase(),
      marketId: m.market_id,
      leverage,
      lastPrice: m.last_trade_price ?? m.mark_price,
      change: m.daily_price_change,
    }
  }).filter((m) => m.symbol)
}

export async function fetchOrderBook(marketId, limit = 14) {
  const url = new URL(`${API_BASE}/orderBookOrders`)
  url.searchParams.set('market_id', String(marketId))
  url.searchParams.set('limit', String(limit))
  const res = await fetch(url)
  if (!res.ok) throw new Error(`orderBookOrders ${res.status}`)
  const json = await res.json()
  const asks = (json.asks || [])
    .map((row) => ({
      price: Number(row.price),
      size: Number(row.remaining_base_amount ?? row.initial_base_amount),
    }))
    .filter((r) => Number.isFinite(r.price) && Number.isFinite(r.size))
    .sort((a, b) => a.price - b.price)
  const bids = (json.bids || [])
    .map((row) => ({
      price: Number(row.price),
      size: Number(row.remaining_base_amount ?? row.initial_base_amount),
    }))
    .filter((r) => Number.isFinite(r.price) && Number.isFinite(r.size))
    .sort((a, b) => b.price - a.price)
  return { asks, bids }
}

export async function fetchRecentTrades(marketId, limit = 30) {
  const url = new URL(`${API_BASE}/recentTrades`)
  url.searchParams.set('market_id', String(marketId))
  url.searchParams.set('limit', String(limit))
  const res = await fetch(url)
  if (!res.ok) throw new Error(`recentTrades ${res.status}`)
  const json = await res.json()
  return (json.trades || []).map((t) => ({
    price: Number(t.price),
    size: Number(t.size),
    timestamp: Number(t.timestamp),
    isBuy: !t.is_maker_ask,
    blockHeight: Number(t.block_height) || 0,
  }))
}

export async function fetchFundings(marketId, countBack = 24) {
  const end = Date.now()
  const start = end - 2 * 24 * 60 * 60 * 1000
  const url = new URL(`${API_BASE}/fundings`)
  url.searchParams.set('market_id', String(marketId))
  url.searchParams.set('resolution', '1h')
  url.searchParams.set('start_timestamp', String(start))
  url.searchParams.set('end_timestamp', String(end))
  url.searchParams.set('count_back', String(countBack))
  const res = await fetch(url)
  if (!res.ok) throw new Error(`fundings ${res.status}`)
  const json = await res.json()
  return json.fundings || []
}

/**
 * @param {{ marketId: number, resolution?: string, countBack?: number }} opts
 */
export async function fetchCandles({
  marketId,
  resolution = '5m',
  countBack = 300,
}) {
  const end = Date.now()
  const spanMs = resolutionToMs(resolution) * Math.max(countBack, 50)
  const start = end - spanMs * 2
  const url = new URL(`${API_BASE}/candles`)
  url.searchParams.set('market_id', String(marketId))
  url.searchParams.set('resolution', resolution)
  url.searchParams.set('start_timestamp', String(start))
  url.searchParams.set('end_timestamp', String(end))
  url.searchParams.set('count_back', String(countBack))

  const res = await fetch(url)
  if (!res.ok) throw new Error(`candles ${res.status}`)
  const json = await res.json()
  const rows = Array.isArray(json.c) ? json.c : []

  const seen = new Set()
  const out = []
  for (const row of rows) {
    const ms = Number(row.t)
    if (!Number.isFinite(ms)) continue
    const time = Math.floor(ms / 1000)
    if (seen.has(time)) continue
    seen.add(time)
    out.push({
      time,
      open: Number(row.o),
      high: Number(row.h),
      low: Number(row.l),
      close: Number(row.c),
      value: Number(row.v) || 0,
    })
  }
  out.sort((a, b) => a.time - b.time)
  return out
}

function resolutionToMs(resolution) {
  const map = {
    '1m': 60_000,
    '5m': 300_000,
    '15m': 900_000,
    '30m': 1_800_000,
    '1h': 3_600_000,
    '4h': 14_400_000,
    '12h': 43_200_000,
    '1d': 86_400_000,
    '1w': 604_800_000,
  }
  return map[resolution] || 300_000
}

export const CHART_RESOLUTIONS = {
  '1m': '1m',
  '5m': '5m',
  '15m': '15m',
  '1h': '1h',
  '4h': '4h',
  '1d': '1d',
}

export function formatPrice(value, decimals = 4) {
  const n = Number(value)
  if (!Number.isFinite(n)) return '-'
  return n.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })
}

export function formatSize(value, decimals = 2) {
  const n = Number(value)
  if (!Number.isFinite(n)) return '-'
  return n.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: decimals,
  })
}

export function formatUsdCompact(value) {
  const n = Number(value)
  if (!Number.isFinite(n)) return '-'
  if (Math.abs(n) >= 1e9) return `$${(n / 1e9).toFixed(2)}B`
  if (Math.abs(n) >= 1e6) return `$${(n / 1e6).toFixed(2)}M`
  if (Math.abs(n) >= 1e3) return `$${(n / 1e3).toFixed(2)}K`
  return `$${n.toFixed(2)}`
}

const POOLS_START_INDEX = 281474976710655
const ZERO_ADDRESS = '0x0000000000000000000000000000000000000000'

/**
 * @param {{ filter?: 'all' | 'user' | 'protocol' | 'stake', limit?: number, maxPages?: number }} [opts]
 */
export async function fetchPublicPools({
  filter = 'all',
  limit = 100,
  maxPages = 30,
} = {}) {
  const pools = []
  let index = POOLS_START_INDEX

  for (let page = 0; page < maxPages; page += 1) {
    const url = new URL(`${API_BASE}/publicPoolsMetadata`)
    url.searchParams.set('index', String(index))
    url.searchParams.set('limit', String(limit))
    if (filter && filter !== 'all') url.searchParams.set('filter', filter)

    const res = await fetch(url)
    if (!res.ok) throw new Error(`publicPoolsMetadata ${res.status}`)
    const json = await res.json()
    const batch = Array.isArray(json.public_pools) ? json.public_pools : []
    if (!batch.length) break

    pools.push(...batch)
    const last = batch[batch.length - 1]
    const nextIndex = Number(last?.account_index)
    if (!Number.isFinite(nextIndex) || nextIndex >= index) break
    index = nextIndex
    if (batch.length < limit) break
  }

  return pools
}

/** Protocol LLP/XLP use zero L1 address; community pools are type 2 with an operator fee. */
export function isPublicPoolAccount(raw) {
  const address = String(raw?.l1_address || '').toLowerCase()
  if (address === ZERO_ADDRESS) return true
  const fee = Number(raw?.operator_fee)
  return Number(raw?.account_type) === 2 && Number.isFinite(fee) && fee > 0
}

export function normalizePublicPool(raw) {
  const tvl = Number(raw.total_asset_value)
  const apr = Number(raw.annual_percentage_yield)
  const sharpe = Number(raw.sharpe_ratio)
  const fee = Number(raw.operator_fee)
  const createdAt = Number(raw.created_at)
  const address = String(raw.l1_address || '')
  const isProtocol = address.toLowerCase() === ZERO_ADDRESS

  const ageDays =
    Number.isFinite(createdAt) && createdAt > 0
      ? Math.max(0, Math.floor((Date.now() / 1000 - createdAt) / 86400))
      : null

  return {
    id: String(raw.account_index),
    name: String(raw.name || 'Untitled Pool'),
    address,
    operatorLabel: isProtocol ? 'Lighter' : truncateAddress(address),
    isProtocol,
    fee: Number.isFinite(fee) ? fee : 0,
    apr: Number.isFinite(apr) ? apr : null,
    sharpe: Number.isFinite(sharpe) ? sharpe : null,
    tvl: Number.isFinite(tvl) ? tvl : 0,
    ageDays,
    yourDeposit: null,
    yourEquity: null,
  }
}

function truncateAddress(address) {
  if (!address || address.length < 10) return address || '-'
  return `${address.slice(0, 6)}...${address.slice(-4)}`
}

export function formatPct(value, digits = 2) {
  const n = Number(value)
  if (!Number.isFinite(n)) return '-'
  const sign = n > 0 ? '+' : ''
  return `${sign}${n.toFixed(digits)}%`
}

export async function fetchExchangeMetrics(period = 'all', kind = 'volume') {
  const url = new URL(`${API_BASE}/exchangeMetrics`)
  url.searchParams.set('period', period)
  url.searchParams.set('kind', kind)
  const res = await fetch(url)
  if (!res.ok) throw new Error(`exchangeMetrics ${res.status}`)
  const json = await res.json()
  return json.metrics || []
}
